import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Alert } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';
import TransactionNotification from './TransactionNotification';

// Debounce utility function
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const SecureServiceBase = ({ 
  show, 
  onHide, 
  onSuccess, 
  serviceName,
  serviceEndpoint,
  children,
  requiresBalance = true,
  minAmount = 0
}) => {
  // const { user } = useAuth(); // Commented out as not used
  const [balance, setBalance] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [showNotification, setShowNotification] = useState(false);
  const [lastTransaction, setLastTransaction] = useState(null);

  // Fetch wallet balance on mount
  useEffect(() => {
    if (show && requiresBalance) {
      fetchBalance();
    }
  }, [show, requiresBalance]);

  const fetchBalance = async () => {
    try {
      setBalanceLoading(true);
      const response = await api.get('/api/wallet/balance');
      if (response.data.success) {
        setBalance(response.data.balance);
      }
    } catch (error) {
      console.error('Failed to fetch balance:', error);
      toast.error('Failed to load wallet balance');
      setBalance(0);
    } finally {
      setBalanceLoading(false);
    }
  };

  // Validation function for amount
  const validateAmount = useCallback((val, currentBalance) => {
    if (!val || isNaN(val) || Number(val) <= 0) {
      setError('Valid amount required');
      return;
    }
    
    if (Number(val) < minAmount) {
      setError(`Minimum amount is ₹${minAmount}`);
      return;
    }
    
    if (Number(val) > Number(currentBalance)) {
      setError('Aapka balance kaafi nahi hai');
    } else {
      setError('');
    }
  }, [minAmount]);

  // Validate amount when it changes
  useEffect(() => {
    if (balance === null || !requiresBalance) {
      setError('');
      return;
    }
    
    // This will be called by child components when amount changes
  }, [balance, requiresBalance]);

  const handleSubmit = async (formData) => {
    if (error || loading) {
      return;
    }

    setLoading(true);
    try {
      // Generate idempotency key for this request
      const idempotencyKey = uuidv4();
      
      const response = await api.post(serviceEndpoint, {
        ...formData,
        idempotency_key: idempotencyKey
      });

      // Only trust server responses with success=true AND transaction_id present
      if (response.data.success && response.data.transaction_id) {
        toast.success(response.data.message || `${serviceName} successful`);
        
        // Update balance
        if (requiresBalance && response.data.new_balance !== undefined) {
          setBalance(response.data.new_balance);
        }
        
        // Show transaction notification
        const transactionData = {
          id: response.data.transaction_id,
          type: 'debit', // Money deducted
          amount: response.data.amount,
          formatted_amount: '₹' + Number(response.data.amount).toFixed(2),
          status: 'success',
          note: response.data.message || `${serviceName} successful`,
          external_ref: response.data.provider_txn_id || response.data.external_ref,
          formatted_date: new Date().toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })
        };
        
        setLastTransaction(transactionData);
        setShowNotification(true);
        
        // Call onSuccess callback for modal
        if (onSuccess) {
          onSuccess({
            transaction_id: response.data.transaction_id,
            amount: response.data.amount,
            new_balance: response.data.new_balance,
            ...response.data
          });
        }
        
        // Close modal after success
        setTimeout(() => {
          if (onHide) onHide();
        }, 2000);
      } else {
        setError(response.data.message || `${serviceName} failed`);
        toast.error(response.data.message || `${serviceName} failed`);
      }
    } catch (error) {
      console.error(`${serviceName} failed:`, error);
      
      if (error.response) {
        const data = error.response.data || {};
        if (data.code === 'INSUFFICIENT_FUNDS') {
          setError('Aapka balance kaafi nahi hai (server)');
          toast.error(data.message || 'Insufficient balance');
        } else if (data.code === 'RETRY_LATER') {
          setError('Request in progress. Please wait...');
          toast.error('Request in progress. Please wait...');
        } else {
          setError(data.message || `${serviceName} failed`);
          toast.error(data.message || `${serviceName} failed`);
        }
      } else {
        setError('Network error');
        toast.error('Network error. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = !error || !requiresBalance;

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" centered>
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>
            <i className="bi bi-shield-check me-2"></i>
            {serviceName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
        {/* Balance Display */}
        {requiresBalance && (
          <div className="mb-4">
            <label className="form-label fw-bold">Available Balance</label>
            <div className="d-flex align-items-center">
              {balanceLoading ? (
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              ) : (
                <span className="fs-4 text-success fw-bold">
                  ₹{balance !== null ? balance.toFixed(2) : '0.00'}
                </span>
              )}
              <button 
                className="btn btn-sm btn-outline-secondary ms-2"
                onClick={fetchBalance}
                disabled={balanceLoading}
              >
                <i className="bi bi-arrow-clockwise"></i> Refresh
              </button>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <Alert variant="danger" className="mb-3">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </Alert>
        )}

        {/* Balance Warning */}
        {requiresBalance && balance !== null && balance === 0 && (
          <Alert variant="danger" className="mb-3" role="alert">
            <i className="bi bi-exclamation-triangle me-2"></i>
            <strong>Insufficient Balance:</strong> Your wallet balance is ₹0. Please add funds to continue.
          </Alert>
        )}
        {requiresBalance && balance !== null && balance > 0 && balance < 100 && (
          <Alert variant="warning" className="mb-3" role="alert">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Low balance warning: Your wallet balance is below ₹100
          </Alert>
        )}

        {/* Children components */}
        {children({
          balance,
          error,
          loading,
          validateAmount,
          handleSubmit,
          isFormValid: isFormValid && !loading
        })}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Transaction Notification */}
      <TransactionNotification
        show={showNotification}
        onHide={() => setShowNotification(false)}
        transaction={lastTransaction}
        onViewStatement={() => {
          // This would open the statement modal
          console.log('View statement clicked');
        }}
      />
    </>
  );
};

export default SecureServiceBase;
